import { useEffect, useMemo, useRef, useState } from 'react'
import maplibregl, {
  LngLatBoundsLike,
  Map as MapLibreInstance,
  Marker,
  StyleSpecification,
} from 'maplibre-gl'
import { Protocol } from 'pmtiles'
import { layers, namedFlavor } from '@protomaps/basemaps'
import 'maplibre-gl/dist/maplibre-gl.css'
import bbox from '@turf/bbox'
import { featureCollection, point } from '@turf/helpers'
import { renderToStaticMarkup } from 'react-dom/server'
import { useTrackerRuntime } from '../runtime'
import {
  fetchMapTileManifest,
  normalizeCountry,
  resolveMapTiles,
} from '../mapTiles'
import { OrderDataInterface } from '../types'
import CarMarker from './Icons/CarIconMarker'
import HomeIconMarker from './Icons/HomeIconMarker'
import MarkerIconMerchant from './Icons/MerchantIcon'

const MAP_FIT_PADDING = 75
const MAP_SOURCE_NAME = 'protomaps'
const BASEMAP_ASSET_REVISION =
  '028c18f713baecad011301ff7a69acc39bcc2ae7'
const protocol = new Protocol()
let protocolRegistered = false

const ensurePmtilesProtocol = () => {
  if (!protocolRegistered) {
    maplibregl.addProtocol('pmtiles', protocol.tile)
    protocolRegistered = true
  }
}

const createMapStyle = (
  mapTilesBaseUrl: string,
  archiveUrl: string
): StyleSpecification => {
  const baseUrl = mapTilesBaseUrl.replace(/\/+$/, '')

  return {
    version: 8,
    glyphs: `${baseUrl}/assets/${BASEMAP_ASSET_REVISION}/fonts/{fontstack}/{range}.pbf`,
    sprite: `${baseUrl}/assets/${BASEMAP_ASSET_REVISION}/sprites/v4/light`,
    sources: {
      [MAP_SOURCE_NAME]: {
        type: 'vector',
        url: `pmtiles://${archiveUrl}`,
        attribution:
          '<a href="https://protomaps.com">Protomaps</a> © <a href="https://www.openstreetmap.org/copyright">OpenStreetMap contributors</a>',
      },
    },
    layers: layers(MAP_SOURCE_NAME, namedFlavor('light')),
  }
}

const toBoundsLike = (box: number[]): LngLatBoundsLike | null =>
  box.length === 4
    ? [
        [box[0], box[1]],
        [box[2], box[3]],
      ]
    : null

const isCoordinate = (value: unknown): boolean => {
  if (typeof value === 'number') {
    return Number.isFinite(value)
  }

  return (
    typeof value === 'string' &&
    value.trim() !== '' &&
    Number.isFinite(Number(value))
  )
}

interface MapLibreMapProps {
  data: OrderDataInterface
}

type MapErrorState =
  | 'country-unavailable'
  | 'manifest-unavailable'
  | 'map-unavailable'

const MapLibreMap = ({ data }: MapLibreMapProps) => {
  const runtime = useTrackerRuntime()
  const mapContainerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<MapLibreInstance | null>(null)
  const markersRef = useRef<Marker[]>([])
  const [archiveUrl, setArchiveUrl] = useState<string | null>(null)
  const [resolvedCountry, setResolvedCountry] = useState<
    string | null
  >(null)
  const [errorState, setErrorState] = useState<MapErrorState | null>(
    null
  )
  const [retryKey, setRetryKey] = useState(0)

  const merchantLatitude = data?.merchant.location.coordinates[1]
  const merchantLongitude = data?.merchant.location.coordinates[0]
  const runnerLatitude = data?.runner?.location?.coordinates?.[1]
  const runnerLongitude = data?.runner?.location?.coordinates?.[0]
  const customerLatitude = data?.customer?.location?.coordinates?.[1]
  const customerLongitude = data?.customer?.location?.coordinates?.[0]
  const status = data.fulfillment?.status
  const country = data.operator?.country
  const mapTilesBaseUrl = runtime.mapTilesBaseUrl

  const finalBbox = useMemo(() => {
    const mapObj: {
      origin: { latitude: number; longitude: number }
      destination: { latitude: number; longitude: number }
      directions: Array<{ latitude: number; longitude: number }>
      runner?: { latitude: number; longitude: number }
    } = {
      origin: {
        latitude: Number(merchantLatitude),
        longitude: Number(merchantLongitude),
      },
      destination: {
        latitude: Number(customerLatitude),
        longitude: Number(customerLongitude),
      },
      directions: [],
    }

    if (
      isCoordinate(runnerLatitude) &&
      isCoordinate(runnerLongitude)
    ) {
      mapObj.runner = {
        latitude: Number(runnerLatitude),
        longitude: Number(runnerLongitude),
      }
    }

    return buildBoundingBox(status, mapObj)
  }, [
    customerLatitude,
    customerLongitude,
    merchantLatitude,
    merchantLongitude,
    runnerLatitude,
    runnerLongitude,
    status,
  ])

  const showCompletedPhoto =
    !!data.order?.dropoff_photo && status === 'completed'
  const latestBboxRef = useRef<number[]>(finalBbox)
  latestBboxRef.current = finalBbox
  const hasFittedRef = useRef(false)

  useEffect(() => {
    if (!errorState || !data.operator?.customer_support_tracking) {
      return undefined
    }

    const atlasWindow = window as Window & {
      Atlas?: { identify: (payload: unknown) => void }
    }

    const timeout = window.setTimeout(() => {
      atlasWindow.Atlas?.identify({
        userId: data.order?.id || data.customer?.address,
        customFields: {
          map_country: resolvedCountry || normalizeCountry(country),
          map_error: errorState,
          map_provider: 'maplibre',
        },
      })
    }, 0)

    return () => window.clearTimeout(timeout)
  }, [
    country,
    data.customer?.address,
    data.operator?.customer_support_tracking,
    data.order?.id,
    errorState,
    resolvedCountry,
  ])

  useEffect(() => {
    let active = true
    let countryResolutionFailed = false
    setArchiveUrl(null)
    setResolvedCountry(null)
    setErrorState(null)

    if (showCompletedPhoto) {
      return () => {
        active = false
      }
    }

    if (!normalizeCountry(country)) {
      setErrorState('country-unavailable')
      return () => {
        active = false
      }
    }

    if (!mapTilesBaseUrl) {
      setErrorState('manifest-unavailable')
      return () => {
        active = false
      }
    }

    fetchMapTileManifest(mapTilesBaseUrl)
      .then(manifest => {
        try {
          return resolveMapTiles(manifest, country)
        } catch (error) {
          countryResolutionFailed = true
          if (active) {
            setErrorState('country-unavailable')
          }
          throw error
        }
      })
      .then(result => {
        if (active) {
          setArchiveUrl(result.archiveUrl)
          setResolvedCountry(result.country)
        }
      })
      .catch(() => {
        if (active && !countryResolutionFailed) {
          setErrorState('manifest-unavailable')
        }
      })

    return () => {
      active = false
    }
  }, [country, mapTilesBaseUrl, retryKey, showCompletedPhoto])

  useEffect(() => {
    if (
      showCompletedPhoto ||
      !archiveUrl ||
      !mapTilesBaseUrl ||
      !mapContainerRef.current ||
      mapRef.current
    ) {
      return
    }

    ensurePmtilesProtocol()
    const initialBounds = toBoundsLike(latestBboxRef.current)
    const map = new maplibregl.Map({
      attributionControl: { compact: true },
      container: mapContainerRef.current,
      style: createMapStyle(mapTilesBaseUrl, archiveUrl),
      ...(initialBounds
        ? {
            bounds: initialBounds,
            fitBoundsOptions: { padding: MAP_FIT_PADDING },
          }
        : {}),
    })

    let sourceErrorCount = 0
    const handleMapError = (event: { sourceId?: string }) => {
      if (event.sourceId !== MAP_SOURCE_NAME) {
        return
      }

      sourceErrorCount += 1
      if (sourceErrorCount >= 2) {
        setErrorState('map-unavailable')
      }
    }
    map.on('error', handleMapError)
    map.on('load', () => map.resize())
    hasFittedRef.current = false
    mapRef.current = map

    return () => {
      map.off('error', handleMapError)
      markersRef.current.forEach(marker => marker.remove())
      markersRef.current = []
      map.remove()
      mapRef.current = null
    }
  }, [archiveUrl, errorState, mapTilesBaseUrl, showCompletedPhoto])

  useEffect(() => {
    if (showCompletedPhoto || !mapRef.current || errorState) {
      return
    }

    const map = mapRef.current
    const updateMap = () => {
      markersRef.current.forEach(marker => marker.remove())
      markersRef.current = []

      const markers: Marker[] = []

      if (
        !['completed', 'cancelled'].includes(status) &&
        isCoordinate(runnerLatitude) &&
        isCoordinate(runnerLongitude)
      ) {
        markers.push(
          new maplibregl.Marker({
            element: createMarkerElement(
              <CarMarker width="28" height="28" status="black" />
            ),
          }).setLngLat([
            Number(runnerLongitude),
            Number(runnerLatitude),
          ])
        )
      }

      markers.push(
        new maplibregl.Marker({
          anchor: 'center',
          element: createMarkerElement(
            <MarkerIconMerchant width="55" height="55" />
          ),
        }).setLngLat([
          Number(merchantLongitude),
          Number(merchantLatitude),
        ])
      )

      markers.push(
        new maplibregl.Marker({
          anchor: 'center',
          element: createMarkerElement(<HomeIconMarker />),
        }).setLngLat([
          Number(customerLongitude),
          Number(customerLatitude),
        ])
      )

      markers.forEach(marker => marker.addTo(map))
      markersRef.current = markers

      const boundsLike = toBoundsLike(finalBbox)
      if (boundsLike) {
        map.fitBounds(boundsLike, {
          padding: MAP_FIT_PADDING,
          animate: hasFittedRef.current,
        })
        hasFittedRef.current = true
      }

      map.resize()
    }

    if (map.isStyleLoaded()) {
      updateMap()
      return
    }

    map.once('load', updateMap)
    return () => {
      map.off('load', updateMap)
    }
  }, [
    customerLatitude,
    customerLongitude,
    errorState,
    finalBbox,
    merchantLatitude,
    merchantLongitude,
    runnerLatitude,
    runnerLongitude,
    showCompletedPhoto,
    status,
  ])

  if (showCompletedPhoto) {
    return (
      <img src={data.order?.dropoff_photo} alt="Delivery Completed" />
    )
  }

  if (errorState) {
    return (
      <div
        data-map-provider="maplibre"
        data-map-country={resolvedCountry || ''}
        data-map-error={errorState}
        className="flex h-full w-full flex-col items-center justify-center gap-3 bg-gray-100 p-6 text-center text-gray-700"
      >
        <p>We couldn&apos;t load the delivery map.</p>
        <a
          href={googleMapsUrl(customerLatitude, customerLongitude)}
          target="_blank"
          rel="noreferrer"
          className="font-semibold underline"
        >
          Open destination in Google Maps
        </a>
        <button
          type="button"
          className="text-sm underline"
          onClick={() => setRetryKey(value => value + 1)}
        >
          Try map again
        </button>
      </div>
    )
  }

  return (
    <div
      ref={mapContainerRef}
      data-map-provider="maplibre"
      data-map-country={resolvedCountry || ''}
      data-map-error=""
      style={{ width: '100%', height: '100%' }}
    />
  )
}

function googleMapsUrl(
  latitude: unknown,
  longitude: unknown
): string {
  const lat = Number(latitude)
  const lng = Number(longitude)
  const query =
    Number.isFinite(lat) && Number.isFinite(lng)
      ? `${lat},${lng}`
      : ''

  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    query
  )}`
}

function createMarkerElement(jsx: JSX.Element) {
  const element = document.createElement('div')
  element.innerHTML = renderToStaticMarkup(jsx)
  return element.firstElementChild as HTMLElement
}

function buildBoundingBox(
  status: string,
  directions: {
    runner?: { latitude: number; longitude: number }
    directions: { latitude: number; longitude: number }[]
    origin: { latitude: number; longitude: number }
    destination: { latitude: number; longitude: number }
  }
): number[] {
  const bboxPoints = []

  try {
    if (
      isCoordinate(directions.origin.longitude) &&
      isCoordinate(directions.origin.latitude)
    ) {
      bboxPoints.push(
        point([
          directions.origin.longitude,
          directions.origin.latitude,
        ])
      )
    }

    if (
      isCoordinate(directions.destination.longitude) &&
      isCoordinate(directions.destination.latitude)
    ) {
      bboxPoints.push(
        point([
          directions.destination.longitude,
          directions.destination.latitude,
        ])
      )
    }

    if (status === 'in_progress' && directions.runner) {
      bboxPoints.push(
        point([
          directions.runner.longitude,
          directions.runner.latitude,
        ])
      )
    }

    return bbox(featureCollection(bboxPoints))
  } catch (_error) {
    return []
  }
}

export default MapLibreMap
